import ReactECharts from 'echarts-for-react';
import { useMemo } from 'react';

import { Skeleton } from '@/components';
import type { PieChartItem } from '@/components/PieChart/types';
import { useThemeColor } from '@/hooks/useThemeColor';
import { formatCurrency } from '@/utils/formatCurrency.ts';

export const SpendBarChart = ({ items, loading }: { items: PieChartItem[]; loading?: boolean }) => {
  const AXIS_LABEL = useThemeColor('--text-muted', '#dbe2ea');
  const AXIS_LINE = useThemeColor('--hairline-strong', 'rgba(255,255,255,0.28)');
  const SPLIT_LINE = useThemeColor('--hairline', 'rgba(255,255,255,0.1)');

  const option = useMemo(
    () => ({
      aria: { enabled: true, label: { enabled: true } },
      grid: { top: 16, right: 12, bottom: 28, left: 56 },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        borderColor: 'rgba(255,255,255,0.2)',
        textStyle: { color: '#f8fafc' },
        valueFormatter: (value: number) => formatCurrency(value),
      },
      xAxis: {
        type: 'category',
        data: items.map((item) => item.title),
        axisLine: { lineStyle: { color: AXIS_LINE } },
        axisTick: { show: false },
        axisLabel: { color: AXIS_LABEL, interval: 0, hideOverlap: true },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: SPLIT_LINE } },
        axisLabel: {
          color: AXIS_LABEL,
          formatter: (value: number) => formatCurrency(value),
        },
      },
      series: [
        {
          type: 'bar',
          data: items.map((item) => ({
            value: item.sum,
            itemStyle: { color: item.color, borderRadius: [6, 6, 0, 0] },
          })),
          barMaxWidth: 40,
        },
      ],
    }),
    [items, AXIS_LABEL, AXIS_LINE, SPLIT_LINE],
  );

  if (loading) {
    // Takes the chart's own box, inset by the grid above so the bars stand where the
    // real ones will. `height="auto"` lifts the variant's fixed 160px.
    return (
      <div
        style={{
          display: 'flex',
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          padding: '16px 12px 28px 56px',
        }}
        aria-busy="true"
      >
        <Skeleton variant="chart" width="100%" height="auto" />
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      style={{ flex: 1, minHeight: 0, height: '100%', width: '100%' }}
      opts={{ renderer: 'svg' }}
    />
  );
};

export default SpendBarChart;
